import {
	CartItemUpsertRule,
	CartItemAvailabilityRule,
	CartStockUpdateRule,
	CartTotalsPreparationRule,
} from '../interfaces';
import { CartItemAvailabilityStatusEnum } from '@core/enums';
import { getCartItemAvailabilityStatus } from '../util/cart-item-availability.util';
import { calculateCartSubtotalByField } from '../util/cart-subtotal.util';

export const cartItemUpsertRule: CartItemUpsertRule = context => {
	const cartItems = context.cartItems ?? [];
	const existingItem = cartItems.some(cartItem => cartItem.idCartItem === context.item.idCartItem);
	const mergedCartItems = existingItem
		? cartItems.map(cartItem =>
				cartItem.idCartItem === context.item.idCartItem ? context.item : cartItem
			)
		: [...cartItems, context.item];

	return { ...context, mergedCartItems };
};

export const cartItemPositiveQuantityRule: CartItemUpsertRule = context => ({
	...context,
	positiveQuantityCartItems: context.mergedCartItems.filter(
		cartItem => (cartItem.cantidad ?? 0n) > 0n
	),
});

export const cartItemStoreSlugRule: CartItemUpsertRule = context => ({
	...context,
	updatedCartItems: context.positiveQuantityCartItems.map(cartItem => ({
		...cartItem,
		storeSlug: !!cartItem.storeSlug ? cartItem.storeSlug : context.storeSlug,
	})),
});

export const cartProductListStockRule: CartStockUpdateRule = context => {
	const { updateProduct } = context;

	return {
		...context,
		updatedProductList: (context.productList ?? []).map(product =>
			product.idArticulo === updateProduct.idArticulo
				? { ...product, stock: updateProduct.stock }
				: { ...product }
		),
	};
};

export const cartProductListAvailabilityRule: CartStockUpdateRule = context => ({
	...context,
	availableProductList: context.updatedProductList.filter(
		product => product.stock === null || product.stock === undefined || product.stock > 0
	),
});

export const cartItemStockRule: CartStockUpdateRule = context => ({
	...context,
	stockAdjustedCartItems: (context.cartItems ?? []).map(cartItem =>
		cartItem.idCartItem === context.updateProduct.idCartItem
			? { ...cartItem, stock: context.updateProduct.stock }
			: { ...cartItem }
	),
});

export const cartItemQuantityLimitRule: CartStockUpdateRule = context => {
	const currentStock = context.updateProduct.stock;

	return {
		...context,
		quantityLimitedCartItems: context.stockAdjustedCartItems.map(cartItem => {
			if (
				cartItem.idCartItem !== context.updateProduct.idCartItem ||
				currentStock === null ||
				currentStock === undefined ||
				currentStock >= Number(cartItem.cantidad ?? 0n)
			) {
				return { ...cartItem };
			}

			return { ...cartItem, cantidad: BigInt(currentStock) };
		}),
	};
};

export const cartItemPositiveQuantityStockRule: CartStockUpdateRule = context => ({
	...context,
	positiveQuantityCartItems: context.quantityLimitedCartItems.filter(
		cartItem => (cartItem.cantidad ?? 0n) > 0n
	),
});

export const cartItemsStockRule: CartStockUpdateRule = context => ({
	...context,
	synchronizedCartItems: context.positiveQuantityCartItems
		.map(cartItem =>
			cartItem.idArticulo === context.updateProduct.idArticulo
				? {
						...cartItem,
						isUnavailable: false,
						stock: context.updateProduct.stock === null || context.updateProduct.stock === undefined
							? null
							: Math.floor(context.updateProduct.stock),
					}
				: { ...cartItem }
		),
});

export const cartItemsStockAvailabilityRule: CartStockUpdateRule = context => ({
	...context,
	updatedCartItems: context.synchronizedCartItems.filter(
		cartItem => cartItem.stock === null || cartItem.stock === undefined || cartItem.stock > 0
	),
});

export const cartTotalsValidityRule: CartTotalsPreparationRule = context => {
	const workDay = context.workDay;
	const isValid =
		context.cartItems !== null &&
		workDay !== null &&
		Number.isInteger(workDay.precisionDecimal) &&
		workDay.precisionDecimal >= 0;

	return {
		...context,
		isValid,
	};
};

export const cartTotalsEligibleItemsRule: CartTotalsPreparationRule = context => ({
	...context,
	eligibleCartItems: context.isValid ? (context.cartItems ?? []) : [],
});

export const cartTotalsSubtotalPriceRule: CartTotalsPreparationRule = context => ({
	...context,
	cartItemsWithSubtotalPrice: context.eligibleCartItems.map(menuItem => ({
		...menuItem,
		price: calculateCartSubtotalByField(menuItem, 'precio'),
		priceCash: calculateCartSubtotalByField(menuItem, 'precioBase'),
	})),
});

export const cartUnavailableItemsRule: CartItemAvailabilityRule = context => ({
	...context,
	unavailableItems: context.cartItems.filter(item =>
		getCartItemAvailabilityStatus({
			item,
			cartItems: context.cartItems,
			availabilityList: context.availabilityList,
			localChannelSession: context.localChannelSession,
			now: context.now,
		}) !== CartItemAvailabilityStatusEnum.Available
	),
});
